import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Image, Platform, Pressable, StyleSheet, View } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { PlaceText } from "./components/PlaceText";
import { PlaceColors } from "./helpers/PlaceColors";
import { getApiFromModule } from "./helpers/GetContentsByModule";
import { getContentVideoUrl } from "./helpers/MediaHelpers";

const targetGroups = [
    {
        standardPoiId: 700,
        accessiblePoiId: 749,
        referenceUrls: ["7V1A6249.jpg", "7V1A6249_1.jpg", "7V1A6249_2.jpg", "7V1A6249_3.jpg", "7V1A6249_4.jpg"],
    },
    {
        standardPoiId: 701,
        accessiblePoiId: 750,
        referenceUrls: ["356A5981.jpg", "356A5981_1.jpg", "356A5981_2.jpg", "356A5981_3.jpg", "356A5981_4.jpg", "356A5981_5.jpg"],
    },
    {
        standardPoiId: 708,
        accessiblePoiId: 754,
        referenceUrls: ["7V1A6098.jpg", "7V1A6098_1.jpg", "7V1A6098_2.jpg", "7V1A6098_3.jpg", "7V1A6098_4.jpg", "7V1A6098_5.jpg", "7V1A6098_6.jpg"],
    },
];

// MindAR reports the index of the matched image. Each image variant is a
// separate target, but all variants in a group open the same POI/video.
const targets = targetGroups.flatMap((group) => group.referenceUrls.map((referenceUrl) => ({
    referenceUrl: `/ar/targets/${referenceUrl}`,
    standardPoiId: group.standardPoiId,
    accessiblePoiId: group.accessiblePoiId,
})));

const fallbackTargets = targetGroups.map((group) => ({
    ...group,
    thumbnailUrl: `/ar/targets/${group.referenceUrls[0]}`,
}));

const loadReferenceImage = (url: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Não foi possível preparar a imagem de referência: ${url}`));
    image.src = url;
});

const loadMindAr = () => new Promise<any>((resolve, reject) => {
    const available = (window as any).MINDAR?.IMAGE;
    if (available) {
        resolve(available);
        return;
    }

    const existing = document.getElementById("findify-mindar-runtime") as HTMLScriptElement | null;
    if (existing) {
        existing.addEventListener("load", () => resolve((window as any).MINDAR?.IMAGE), { once: true });
        existing.addEventListener("error", () => reject(new Error("Não foi possível carregar o reconhecimento visual.")), { once: true });
        return;
    }

    const script = document.createElement("script");
    script.id = "findify-mindar-runtime";
    script.type = "module";
    script.src = "/ar/mindar-image.prod.js";
    script.onload = () => resolve((window as any).MINDAR?.IMAGE);
    script.onerror = () => reject(new Error("Não foi possível carregar o reconhecimento visual."));
    document.head.appendChild(script);
});

export const ScanRATab = ({ navigation }: any) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const colors = PlaceColors(findifyDataContext);
    const scannerContainer = useRef<any>(null);
    const scannerRef = useRef<any>(null);
    const targetUrlRef = useRef<string | null>(null);
    const detectedRef = useRef(false);
    const candidateTargetRef = useRef<number | null>(null);
    const candidateFramesRef = useRef(0);
    const [status, setStatus] = useState("A preparar reconhecimento visual…");
    const [error, setError] = useState("");

    const openPoiVideo = useCallback(async (poiId: number) => {
        if (detectedRef.current) return;
        detectedRef.current = true;
        setStatus("Objeto reconhecido. A abrir vídeo…");

        try {
            scannerRef.current?.stop?.();
            const poi = await getApiFromModule("points_of_interest", findifyDataContext)?.get(poiId, null, null);
            const videoUrl = getContentVideoUrl(poi, findifyDataContext?.language);

            if (!videoUrl) throw new Error("O POI reconhecido não tem vídeo disponível neste idioma.");

            navigation.replace("ARVideoPlayer", { videoUrl, poiId });
        } catch (recognitionError: any) {
            console.error("Failed to open recognized AR video:", recognitionError);
            detectedRef.current = false;
            setError(recognitionError?.message ?? "Não foi possível abrir o vídeo deste objeto.");
            setStatus("Aponte novamente a câmara para o objeto.");
        }
    }, [findifyDataContext, navigation]);

    const openTargetVideo = useCallback(async (targetIndex: number) => {
        const target = targets[targetIndex];
        if (!target) return;
        const poiId = findifyDataContext?.visitVersion === "accessible"
            ? target.accessiblePoiId
            : target.standardPoiId;
        await openPoiVideo(poiId);
    }, [findifyDataContext, openPoiVideo]);

    useEffect(() => {
        if (Platform.OS !== "web" || typeof window === "undefined" || !scannerContainer.current) return;

        let cancelled = false;
        const startScanner = async () => {
            try {
                const { Compiler, Controller } = await loadMindAr();
                const cache = await caches.open("findify-ar-targets");
                const cacheKey = "/findify-ar-targets/biscainhos-v12.mind";
                const cachedTarget = await cache.match(cacheKey);
                let targetData: ArrayBuffer;

                if (cachedTarget) {
                    targetData = await cachedTarget.arrayBuffer();
                    setStatus("A preparar a câmara…");
                } else {
                    const bundledTarget = await fetch("/ar/targets/biscainhos-v12.mind");
                    if (bundledTarget.ok) {
                        targetData = await bundledTarget.arrayBuffer();
                        await cache.put(cacheKey, new Response(targetData.slice(0)));
                        setStatus("A preparar a câmara…");
                    } else {
                        const images = await Promise.all(targets.map((target) => loadReferenceImage(target.referenceUrl)));
                        if (cancelled) return;

                        const compiler = new Compiler();
                        await compiler.compileImageTargets(images, (progress: number) => {
                            if (!cancelled) setStatus(`A preparar reconhecimento visual… ${Math.round(progress)}%`);
                        });
                        targetData = await compiler.exportData();
                        await cache.put(cacheKey, new Response(targetData.slice(0)));
                    }
                }
                targetUrlRef.current = URL.createObjectURL(new Blob([targetData]));
                if (cancelled) return;

                const video = document.createElement("video");
                video.autoplay = true;
                video.muted = true;
                video.playsInline = true;
                video.style.cssText = "width:100%;height:100%;object-fit:contain;background:#000;";
                scannerContainer.current.replaceChildren(video);
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: "environment" },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                    audio: false,
                });
                video.srcObject = stream;
                await new Promise<void>((resolve) => video.addEventListener("loadedmetadata", () => resolve(), { once: true }));
                // MindAR reads the element's drawing-buffer dimensions while
                // extracting visual features. Set these explicitly rather than
                // relying only on the CSS dimensions of the camera preview.
                video.width = video.videoWidth;
                video.height = video.videoHeight;
                await video.play();

                const controller = new Controller({
                    inputWidth: video.videoWidth,
                    inputHeight: video.videoHeight,
                    maxTrack: 1,
                    onUpdate: (data: any) => {
                        if (data?.type !== "updateMatrix") return;

                        if (data.worldMatrix === null) {
                            candidateTargetRef.current = null;
                            candidateFramesRef.current = 0;
                            return;
                        }

                        if (candidateTargetRef.current === data.targetIndex) {
                            candidateFramesRef.current += 1;
                        } else {
                            candidateTargetRef.current = data.targetIndex;
                            candidateFramesRef.current = 1;
                        }

                        if (candidateFramesRef.current >= 3) openTargetVideo(data.targetIndex);
                    },
                });
                await controller.addImageTargets(targetUrlRef.current);
                await controller.dummyRun(video);
                controller.processVideo(video);
                scannerRef.current = {
                    stop: () => {
                        controller.stopProcessVideo();
                        stream.getTracks().forEach((track) => track.stop());
                        video.remove();
                    },
                };
                if (!cancelled) setStatus("Aponte a câmara para a estátua, os azulejos ou a pintura.");
            } catch (scannerError) {
                console.error("Failed to start AR scanner:", scannerError);
                if (!cancelled) setError("Não foi possível iniciar o reconhecimento. Confirme a permissão da câmara e tente novamente.");
            }
        };

        startScanner();
        return () => {
            cancelled = true;
            try { scannerRef.current?.stop?.(); } catch (_) { /* Scanner may not have started yet. */ }
            scannerRef.current = null;
            if (targetUrlRef.current) URL.revokeObjectURL(targetUrlRef.current);
            targetUrlRef.current = null;
        };
    }, [openTargetVideo]);

    if (Platform.OS !== "web") {
        return <View style={[styles.fallback, { backgroundColor: colors.background.get() }]}>
            <PlaceText style={[styles.fallbackText, { color: colors.text.get() }]}>O reconhecimento visual está disponível na versão web da aplicação.</PlaceText>
        </View>;
    }

    return <View style={[styles.page, { backgroundColor: "#000" }]}>
        {React.createElement("div", { ref: scannerContainer, style: styles.camera as any })}
        <View pointerEvents="box-none" style={styles.overlay}>
            <Pressable style={[styles.backButton, { backgroundColor: colors.primary.withAlpha(225) }]} onPress={() => navigation.goBack()}>
                <Icon name="chevron-back" size={29} color="#fff" />
            </Pressable>
            <View style={styles.frame} />
            <PlaceText style={styles.status}>{error || status}</PlaceText>
            <View style={styles.fallbackPanel}>
                <PlaceText style={styles.fallbackTitle}>Abrir vídeo diretamente</PlaceText>
                <View style={styles.fallbackTargets}>
                    {fallbackTargets.map((target) => {
                        const poiId = findifyDataContext?.visitVersion === "accessible"
                            ? target.accessiblePoiId
                            : target.standardPoiId;
                        return <Pressable
                            key={target.standardPoiId}
                            style={styles.fallbackTarget}
                            onPress={() => openPoiVideo(poiId)}
                            accessibilityLabel="Abrir vídeo deste ponto"
                        >
                            <Image source={{ uri: target.thumbnailUrl }} style={styles.fallbackImage} />
                        </Pressable>;
                    })}
                </View>
            </View>
        </View>
    </View>;
};

const styles = StyleSheet.create({
    page: { flex: 1, position: "relative", overflow: "hidden" },
    camera: { width: "100%", height: "100%", position: "relative", overflow: "hidden", backgroundColor: "#000" },
    overlay: { ...StyleSheet.absoluteFillObject, alignItems: "center", justifyContent: "center", padding: 24, backgroundColor: "rgba(0,0,0,0.18)" },
    backButton: { position: "absolute", top: 18, left: 18, width: 48, height: 48, borderRadius: 24, alignItems: "center", justifyContent: "center" },
    frame: { width: 245, height: 245, borderColor: "#fff", borderWidth: 3, borderRadius: 18 },
    status: { position: "absolute", left: 24, right: 24, bottom: 44, color: "#fff", fontSize: 16, fontWeight: "700", textAlign: "center", textShadowColor: "rgba(0,0,0,0.7)", textShadowRadius: 4 },
    fallbackPanel: { position: "absolute", left: 18, right: 18, bottom: 88, alignItems: "center" },
    fallbackTitle: { color: "#fff", fontSize: 13, fontWeight: "700", marginBottom: 8, textShadowColor: "rgba(0,0,0,0.8)", textShadowRadius: 4 },
    fallbackTargets: { flexDirection: "row", gap: 10 },
    fallbackTarget: { width: 72, height: 54, borderRadius: 8, overflow: "hidden", borderWidth: 2, borderColor: "rgba(255,255,255,0.9)", backgroundColor: "#222" },
    fallbackImage: { width: "100%", height: "100%", resizeMode: "cover" },
    fallback: { flex: 1, justifyContent: "center", alignItems: "center", padding: 28 },
    fallbackText: { fontSize: 16, textAlign: "center", lineHeight: 24 },
});
